import { Flex, Button, Input, Modal } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { Text, Paragraph } from "ui/Typography"
import { spacings } from "themes/variables"
import { StatusBadge, VERIFICATION_STATUS } from "./StatusBadge"
import { DkimRecordsTable } from "./DkimRecordsTable"
import { useEmailDomain } from "./useEmailDomain"
import styles from "./CustomEmailTab.module.css"

export const CustomEmailTab = () => {
  const { t } = useTranslation()
  const {
    domain,
    from_address,
    from_name,
    verification_status,
    dkim_records,
    spf_record,
    loading,
    updateLoading,
    refreshLoading,
    removeLoading,
    domainInput,
    setDomainInput,
    fromAddressInput,
    setFromAddressInput,
    fromNameInput,
    setFromNameInput,
    removeModalOpen,
    setRemoveModalOpen,
    handleRegisterDomain,
    handleSaveFromAddress,
    handleRefreshStatus,
    handleRemoveDomain
  } = useEmailDomain()

  if (!domain) {
    return (
      <Flex vertical gap={spacings["XL"]} className={styles.wrapper}>
        <Paragraph size="md">{t("customEmail.setup.description")}</Paragraph>

        <Flex vertical gap={spacings.S}>
          <label className={styles.label}>{t("customEmail.setup.domain_label")}</label>
          <Input
            placeholder={t("customEmail.setup.domain_placeholder")}
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            className={styles.domainInput}
            size="large"
          />
          <Text size="sm" color="secondary">
            {t("customEmail.setup.hint")}
          </Text>
        </Flex>
        <Flex vertical gap={spacings.S}>
          <label className={styles.label}>{t("customEmail.from_name.label")}</label>
          <Input
            placeholder={t("customEmail.from_name.placeholder")}
            value={fromNameInput}
            onChange={(e) => setFromNameInput(e.target.value)}
            className={styles.domainInput}
            size="large"
          />
        </Flex>
        <Flex vertical gap={spacings.S}>
          <label className={styles.label}>{t("customEmail.from_address.label")}</label>
          <Input
            placeholder={t("customEmail.from_address.placeholder", { domain: domainInput.trim() || "yourdomain.com" })}
            value={fromAddressInput}
            onChange={(e) => setFromAddressInput(e.target.value)}
            className={styles.domainInput}
            size="large"
          />
          <Text size="sm" color="secondary">
            {t("customEmail.from_address.setup_hint")}
          </Text>
        </Flex>

        <div className="mb-4">
          <Button type="primary" size="large" loading={loading} disabled={!domainInput.trim() || loading} onClick={handleRegisterDomain}>
            {t("customEmail.setup.cta")}
          </Button>
        </div>
      </Flex>
    )
  }
  return (
    <Flex vertical gap={spacings["3XL"]} className={styles.wrapper}>
      <Flex align="center" gap={spacings.M} wrap="wrap">
        <Flex align="center" gap={spacings.XS}>
          <Icon type="feather" name="Globe" width={18} height={18} />
          <Text size="md" bold>
            {domain}
          </Text>
        </Flex>
        <StatusBadge status={verification_status} />
        <Button variant="primary" danger onClick={() => setRemoveModalOpen(true)} className="ml-auto">
          {t("customEmail.remove.trigger")}
        </Button>
      </Flex>

      <Flex vertical gap={spacings.S}>
        <Text size="md" bold>
          {t("customEmail.from_address.title")}
        </Text>
        <Paragraph size="sm">{t("customEmail.from_address.description", { domain })}</Paragraph>
        {verification_status === VERIFICATION_STATUS.pending && (
          <Text size="sm" color="secondary">
            {t("customEmail.from_address.pending_hint")}
          </Text>
        )}
        <Input
          placeholder={t("customEmail.from_name.placeholder")}
          value={fromNameInput}
          onChange={(e) => setFromNameInput(e.target.value)}
          onPressEnter={handleSaveFromAddress}
          className={styles.domainInput}
          size="large"
        />
        <Flex align="center" gap={spacings.S} wrap="wrap">
          <Input
            placeholder={`e.g. bookings@${domain}`}
            value={fromAddressInput}
            onChange={(e) => setFromAddressInput(e.target.value)}
            onPressEnter={handleSaveFromAddress}
            className={styles.domainInput}
            size="large"
          />
          <Button
            type="primary"
            size="large"
            loading={updateLoading}
            disabled={!fromAddressInput.trim() || (fromAddressInput === from_address && fromNameInput === from_name)}
            onClick={handleSaveFromAddress}
          >
            {t("customEmail.from_address.save")}
          </Button>
        </Flex>
      </Flex>

      <Flex vertical gap={spacings.S} className="mb-4">
        <Flex align="center" gap={spacings.S}>
          <Text size="md" bold>
            {t("customEmail.dkim.title")}
          </Text>
          {verification_status === VERIFICATION_STATUS.pending && (
            <Button
              variant="outlined"
              color="primary"
              icon={<Icon type="feather" name="RefreshCw" width={14} height={14} />}
              loading={refreshLoading}
              onClick={handleRefreshStatus}
            >
              {t("customEmail.dkim.check_status")}
            </Button>
          )}
        </Flex>
        <Paragraph size="sm">{t("customEmail.dkim.description")}</Paragraph>
        <DkimRecordsTable records={dkim_records} spfRecord={spf_record} />
      </Flex>

      {removeModalOpen && (
        <Modal
          open={removeModalOpen}
          title={t("customEmail.remove.modal_title")}
          okText={t("customEmail.remove.confirm")}
          cancelText={t("global.cancel")}
          okButtonProps={{ danger: true, loading: removeLoading }}
          onOk={handleRemoveDomain}
          onCancel={() => setRemoveModalOpen(false)}
          centered
        >
          <Paragraph size="md">{t("customEmail.remove.modal_body", { domain })}</Paragraph>
        </Modal>
      )}
    </Flex>
  )
}
